import { Row, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import EditIcon from "../../assets/icons/pen-dark-bg.svg";
import AudioRecorderComponent from "../AudioRecorderComponent/AudioRecorderComponent";
import "./RecordAudioModal.less";

interface RecordAudioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAudioReady?: (audioBlob: Blob, audioTitle: string, duration: number) => void;
  isFromConv?: boolean;
  onUploadSuccess?: () => void; // Callback when upload is successful
  onOpenQuickAudioModals?: () => void; // Open quick-files audio selection modals instead of navigating
}

const RecordAudioModal: React.FC<RecordAudioModalProps> = ({
  isOpen,
  onClose,
  onAudioReady,
  isFromConv = false,
  onUploadSuccess,
  onOpenQuickAudioModals,
}) => {
  const { t } = useTranslation();
  // Use empty string as fallback instead of potentially null value
  const untitledText = t("untitled") || "Untitled";
  const [audioTitle, setAudioTitle] = useState(untitledText);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [recorderKey, setRecorderKey] = useState(0);

  // Reset audio title and force recorder to remount when modal opens
  useEffect(() => {
    if (isOpen) {
      setAudioTitle(t("untitled") || "Untitled");
      setIsEditingTitle(false);
      // Clear the session storage to prevent restoring old audio
      sessionStorage.removeItem("currentAudioSessionId");
      // Force AudioRecorderComponent to remount with a new key
      setRecorderKey((prev) => prev + 1);
    }
  }, [isOpen, t]);

  useEffect(() => {
    const preventTouchMove = (e: Event) => {
      e.preventDefault();
    };
    if (isOpen) {
      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }
    return () => {
      document.body.removeEventListener("touchmove", preventTouchMove);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = () => onClose();
  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const handleAudioReady = (audioBlob: Blob, titleFromRecorder: string, duration: number) => {
    if (onAudioReady) {
      onAudioReady(audioBlob, audioTitle, duration);
    } else {
      onClose();
    }
  };

  const handleTitleEdit = () => {
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAudioTitle(e.target.value);
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setIsEditingTitle(false);
    }
  };

  const handleAudioCancel = () => {
    onClose();
  };

  return (
    <div className="bottom-modal-overlay" onClick={handleOverlayClick}>
      <div className="bottom-modal-content" onClick={handleModalClick}>
        <Row className="w-full justify-content-center mt-10">
          <div className="header-icon-grey" onClick={handleOverlayClick}></div>
        </Row>

        <div className="bottom-modal-container">
          {!isFromConv && (
            <div className="bottom-modal-title-container">
              {isEditingTitle ? (
                <input
                  type="text"
                  className="bottom-modal-title-input"
                  value={audioTitle}
                  onChange={handleTitleChange}
                  onBlur={handleTitleBlur}
                  onKeyDown={handleTitleKeyDown}
                  onFocus={(e) => e.currentTarget.select()}
                  autoFocus
                />
              ) : (
                <div className="bottom-modal-title-wrapper">
                  <div className="bottom-modal-title">{audioTitle}</div>
                  <img src={EditIcon} alt="edit" className="bottom-modal-edit-icon" onClick={handleTitleEdit} />
                </div>
              )}
            </div>
          )}
          <AudioRecorderComponent
            key={recorderKey}
            onAudioReady={handleAudioReady}
            onCancel={handleAudioCancel}
            maxDuration={300}
            audioTitle={audioTitle || ""}
            isFromConv={isFromConv}
            onUploadSuccess={onUploadSuccess}
            onOpenQuickAudioModals={onOpenQuickAudioModals}
          />
          <button className="cancel-button" onClick={onClose}>
            <Typography.Text className="cancel-button-title">{t("cancel")}</Typography.Text>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordAudioModal;
